// src/components/ui/AccessLevelIndicator.jsx
import React from 'react';
import { Shield, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

/**
 * Component to show user's access level and permission status
 */
export const AccessLevelIndicator = ({ 
  className = "",
  showIcon = true,
  showText = true,
  size = "sm"
}) => {
  const { user, userRoleDisplayName, isAdmin, isSuperAdmin } = useAuth();

  if (!user) return null;

  const sizeClasses = {
    xs: 'text-xs px-1.5 py-0.5',
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-1.5',
    lg: 'text-base px-4 py-2'
  };

  const iconSizes = {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 18
  };

  const getBadgeStyle = () => {
    if (isSuperAdmin) {
      return 'bg-purple-100 text-purple-800 border border-purple-200';
    } else if (isAdmin) {
      return 'bg-green-100 text-green-800 border border-green-200';
    } else {
      return 'bg-blue-100 text-blue-800 border border-blue-200';
    }
  };

  const getIcon = () => {
    if (isSuperAdmin) return Shield;
    if (isAdmin) return Lock;
    return Eye;
  };

  const Icon = getIcon();

  return (
    <div className={`inline-flex items-center gap-1.5 rounded-full font-medium ${sizeClasses[size]} ${getBadgeStyle()} ${className}`}>
      {showIcon && <Icon size={iconSizes[size]} />}
      {showText && <span>{userRoleDisplayName}</span>}
    </div>
  );
};

/**
 * Component to show permission status for specific features
 */
export const PermissionIndicator = ({ 
  permission,
  permissions = [],
  requireAll = false,
  className = "",
  showText = false,
  size = "sm"
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = useAuth();

  const iconSizes = {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 18
  };

  let hasAccess = false;
  
  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions.length > 0) {
    hasAccess = requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);
  }

  const Icon = hasAccess ? Eye : EyeOff;
  const colorClass = hasAccess ? 'text-green-600' : 'text-gray-400';

  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      <Icon size={iconSizes[size]} className={colorClass} />
      {showText && (
        <span className={`text-${size} ${colorClass}`}>
          {hasAccess ? 'Accessible' : 'Restricted'}
        </span>
      )}
    </div>
  );
};

/**
 * Component to show route access status
 */
export const RouteAccessIndicator = ({ 
  route,
  className = "",
  showText = false,
  size = "sm"
}) => {
  const { canAccessRoute } = useAuth();

  const iconSizes = {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 18
  };

  const hasAccess = canAccessRoute(route);
  const Icon = hasAccess ? Eye : EyeOff;
  const colorClass = hasAccess ? 'text-green-600' : 'text-red-500';

  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      <Icon size={iconSizes[size]} className={colorClass} />
      {showText && (
        <span className={`text-${size} ${colorClass}`}>
          {hasAccess ? 'Accessible' : 'Restricted'}
        </span>
      )}
    </div>
  );
};

/**
 * Higher-order component to conditionally render content based on permissions
 */
export const ConditionalAccess = ({ 
  permission,
  permissions = [],
  requireAll = false,
  fallback = null,
  showFallback = false,
  children 
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = useAuth();

  let hasAccess = false;
  
  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions.length > 0) {
    hasAccess = requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);
  }

  if (hasAccess) {
    return children;
  }

  if (showFallback && fallback) {
    return fallback;
  }

  return null;
};

/**
 * Component to show access level summary
 */
export const AccessSummary = ({ className = "" }) => {
  const { user, isAdmin, isSuperAdmin, isUser } = useAuth();

  if (!user) return null;

  const accessLevels = [
    { label: 'User Access', active: isUser, color: 'blue' },
    { label: 'Admin Access', active: isAdmin, color: 'green' },
    { label: 'Super Admin Access', active: isSuperAdmin, color: 'purple' }
  ];

  return (
    <div className={`flex gap-2 ${className}`}>
      {accessLevels.map(({ label, active, color }) => (
        <div
          key={label}
          className={`text-xs px-2 py-1 rounded-full font-medium ${
            active 
              ? `bg-${color}-100 text-${color}-800 border border-${color}-200` 
              : 'bg-gray-100 text-gray-400 border border-gray-200'
          }`}
        >
          {label}
        </div>
      ))}
    </div>
  );
};
